<template>
  <div class="singer-list-wrapper">
    <PixelCatRunner :speed="1" :cat-type="1" />
    <!-- 搜索栏 -->
    <div class="search-bar-wrapper">
      <div class="search-bar">
        <span class="search-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </svg>
        </span>
        <input type="text" v-model="searchKeyword" placeholder="搜索歌手或歌曲..." />
        <div class="search-type-toggle">
          <button class="search-type-btn" :class="{ active: searchType === 'all' }" @click="setSearchType('all')">全部</button>
          <button class="search-type-btn" :class="{ active: searchType === 'singer' }" @click="setSearchType('singer')">歌手</button>
          <button class="search-type-btn" :class="{ active: searchType === 'song' }" @click="setSearchType('song')">歌曲</button>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-screen">
      <div class="loading-animation"></div>
      <div class="loading-text">正在加载歌手信息...</div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="allSingers.length === 0" class="empty-state">
      <div class="empty-icon">🎵</div>
      <div class="empty-text">暂无歌手信息</div>
    </div>

    <div v-else-if="filteredSingers.length === 0" class="empty-state">
      <div class="empty-icon">🔍</div>
      <div class="empty-text">没有找到匹配的结果</div>
    </div>

    <!-- 3D 轮播图 -->
    <SingerCarousel
      v-else
      :singers="filteredSingers"
      @select-singer="onSelectSinger"
    />
  </div>
</template>

<script>
import SingerCarousel from './SingerCarousel.vue';
import PixelCatRunner from './PixelCatRunner.vue';

export default {
  name: 'SingerList',
  components: {
    SingerCarousel,
    PixelCatRunner,
  },
  props: {
    allSingers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      searchType: 'all',
      searchKeyword: '',
    };
  },
  computed: {
    filteredSingers() {
      return this.filterSingers(this.allSingers, this.searchKeyword, this.searchType);
    },
  },
  methods: {
    filterSingers(singers, keyword, type) {
      if (!keyword.trim()) return singers;
      const kw = keyword.trim().toLowerCase();

      return singers.filter((singer) => {
        const matchSinger = singer.name.toLowerCase().includes(kw) ||
            this.splitTags(singer.genre).some((t) => t.toLowerCase().includes(kw)) ||
            this.splitTags(singer.timbre).some((t) => t.toLowerCase().includes(kw));

        const matchSong = (singer.songs || []).some((song) => {
          return song.title.toLowerCase().includes(kw);
        });

        if (type === 'singer') return matchSinger;
        if (type === 'song') return matchSong;
        return matchSinger || matchSong;
      });
    },
    setSearchType(type) {
      this.searchType = type;
    },
    splitTags(str) {
      if (!str) return [];
      return str.split(/[,，、/]+/).map((t) => t.trim()).filter(Boolean);
    },
    onSelectSinger(singer) {
      this.$emit('open-songlist', singer);
    },
  },
}
</script>

<style scoped>
.search-bar-wrapper {
  margin: 32px 0 0;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0;
  background: var(--glass-strong);
  border: 3px solid var(--pixel-dark);
  border-radius: 6px;
  padding: 5px 6px 5px 28px;
  transition: var(--transition-bounce);
  box-shadow: 0 5px 16px var(--shadow-soft), inset 0 1px 0 rgba(255, 255, 255, 0.7);
  position: relative;
  overflow: hidden;
}

.search-bar:focus-within {
  border-color: var(--pixel-lavender);
  box-shadow: 0 8px 28px rgba(123, 147, 219, 0.3), 0 0 40px var(--glow-blue);
  transform: scale(1.02);
}

.search-bar .search-icon {
  color: var(--dream-accent);
  flex-shrink: 0;
  margin-right: 12px;
}

.search-bar input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: var(--text-primary);
  padding: 12px 0;
  font-family: inherit;
}

.search-bar input::placeholder {
  color: var(--text-light);
  opacity: 0.75;
}

.search-type-toggle {
  display: flex;
  gap: 6px;
  background: var(--glass-bg);
  border-radius: 3px;
  padding: 5px;
  margin-right: 6px;
  border: 2px solid var(--pixel-dark);
}

.search-type-btn {
  border: 2px solid transparent;
  background: transparent;
  padding: 9px 20px;
  border-radius: 3px;
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: var(--transition-bounce);
  font-family: inherit;
  font-weight: 600;
}

.search-type-btn.active {
  background: linear-gradient(180deg, var(--pixel-blue), var(--pixel-lavender));
  color: var(--pixel-dark);
  box-shadow: 0 5px 16px rgba(123, 147, 219, 0.45);
  border-color: var(--pixel-dark);
}

.search-type-btn:hover:not(.active) {
  background: var(--glass-bg);
  transform: translateY(-2px);
}

/* 加载状态 */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 350px;
  gap: 20px;
}

.loading-animation {
  width: 56px;
  height: 56px;
  border: 5px solid var(--glass-bg);
  border-top-color: var(--dream-accent);
  border-right-color: var(--dream-lavender);
  border-radius: 50%;
  animation: spinEnhanced 1s linear infinite;
  box-shadow: 0 0 20px var(--glow-blue);
}

@keyframes spinEnhanced {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: var(--text-secondary);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 56px 24px;
  color: var(--text-secondary);
}

.empty-state .empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.6;
  animation: floatIcon 3s ease-in-out infinite;
}

@keyframes floatIcon {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.empty-state .empty-text {
  font-size: 16px;
}

@media (max-width: 768px) {
  .search-bar {
    flex-wrap: wrap;
    padding: 10px;
    border-radius: 24px;
  }

  .search-bar input {
    width: 100%;
    order: 2;
    padding: 8px 12px;
  }

  .search-type-toggle {
    order: 1;
    width: 100%;
    justify-content: center;
    margin-right: 0;
    margin-bottom: 8px;
  }

  .search-bar .search-icon {
    display: none;
  }
}

@media (max-width: 480px) {
  .search-bar {
    padding: 8px;
    border-radius: 20px;
  }

  .search-bar input {
    font-size: 14px;
    padding: 7px 10px;
  }

  .search-type-btn {
    padding: 7px 14px;
    font-size: 13px;
  }

  .loading-screen {
    min-height: 260px;
  }
}
</style>